/**
 * AppLayout — Sprint 2
 * Agrega subnav de Ventas (Clientes / Productos) en el sidebar.
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import { useCierreAlClicAfuera } from '@/lib/useCierreAlClicAfuera'
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  LayoutDashboard, Users, Settings, ShoppingCart, ShoppingBag,
  BookOpen, Landmark, ChevronDown, ChevronRight, LogOut, User,
  KeyRound, Building2, Menu, X, RefreshCw, Package, UserSquare2, FileText, FileSignature,
  Truck, ClipboardList, Banknote, CreditCard, Repeat, BarChart3, Settings2,
  Shield, Globe, Hash, ListPlus, Zap, ShieldAlert, DollarSign, Copy, FileCheck, FileDown,
  ArrowLeftRight, TrendingDown, Mail, Calendar, AlertTriangle, Receipt,
  Bell, FolderOpen, Lock, Gift, Activity, Upload, Paperclip,
  Boxes, Tags, PanelLeftOpen, Briefcase, Inbox, BadgeCheck, Network, Store, MonitorSmartphone, UserCog, Wallet, Printer, ScanBarcode,
} from 'lucide-react'
import { NotificationBell } from '@/modules/notifications/components/NotificationBell'
import { CartBell } from '@/modules/sales/components/CartBell'
import { UpdatesTrigger } from '@/modules/platform-updates/components/UpdatesTrigger'
import { WorkspaceSwitcher } from '@/app/layouts/WorkspaceSwitcher'
import { OrbField } from '@/components/ds/OrbField'
import { TourProvider } from '@/lib/tours/TourContext'
import { etiquetasNumeracion } from '@/lib/documentNumbering'
import { etiquetaDeEstadoInactivo } from '@/lib/tenantStatus'
import { QuickThemeToggle } from '@/components/ThemeToggle'
import { EnvBanner } from '@/components/EnvBanner'
import { MaintenanceBanner } from '@/components/MaintenanceBanner'
import { SiiStatusBanner } from '@/components/SiiStatusBanner'
import { SeasonalBanner } from '@/components/SeasonalBanner'
import { useWebSocket } from '@/hooks/useWebSocket'
import { useAuthStore } from '@/store/authStore'
import { useLogout, useMe } from '@/modules/auth/hooks/useAuth'
import { useCompanyProfile } from '@/modules/config/hooks/useConfig'
import { ROLE_NAMES, type RoleType } from '@/types'
import { usePermission } from '@/hooks/usePermission'
import { useAccountStatus } from '@/modules/config/hooks/useConfig'

// ─── Banner Cuenta Trial/Demo ─────────────────────────────────────────────────

function AccountBanner() {
  const { data: status } = useAccountStatus()
  const user = useAuthStore(s => s.user)

  /* 🔴 Un espacio de trabajo que no está activo tiene que decirlo, sea del tipo
     que sea. Reportado el 2026-08-27: se desactivaron dos tenants en staging y
     desde la cuenta de plataforma se podía entrar y navegar como si nada.

     El acceso es correcto y deliberado —`tenant_access_blocked` exime al
     platform-admin justamente para poder diagnosticar o reactivar, y para
     cualquier otro usuario el middleware responde 403— pero la pantalla no daba
     ninguna señal, así que parecía que la desactivación no había surtido efecto.
     El banner de cuenta salía antes solo para trial/demo, y el tenant del reporte
     era `production`. */
  const estadoInactivo = etiquetaDeEstadoInactivo(status?.status)

  if (estadoInactivo) {
    return (
      <div className="shrink-0 border-b border-danger-border bg-danger-surface px-4 py-2 flex items-center gap-2 text-xs font-medium text-danger-content-strong">
        <AlertTriangle size={13} className="shrink-0" />
        <span>
          Este espacio de trabajo está <strong>{estadoInactivo}</strong>
          {user?.is_platform_admin
            ? ' — lo estás viendo porque tu cuenta es de administrador de la plataforma. Para los demás usuarios el acceso está bloqueado.'
            : ' — el acceso está restringido. Contacta al administrador de la plataforma.'}
        </span>
      </div>
    )
  }

  if (!status || status.account_type === 'production') return null

  const isTrial = status.account_type === 'trial'
  const isDemo = status.account_type === 'demo'

  const bgCls = isTrial ? 'bg-warning-surface border-warning-border text-warning-content-strong'
               : isDemo  ? 'bg-info-surface border-info-border text-info-content-strong'
               : 'bg-surface-muted border-divider text-content'

  return (
    <div className={`shrink-0 border-b px-4 py-2 flex items-center gap-2 text-xs font-medium ${bgCls}`}>
      <AlertTriangle size={13} className="shrink-0" />
      <span>
        {isTrial && (
          <>
            Cuenta <strong>Trial</strong>
            {status.days_remaining !== null && (
              <> — Vence en <strong>{status.days_remaining} día{status.days_remaining !== 1 ? 's' : ''}</strong></>
            )}
            {status.allow_homologation
              ? ' · Habilitado para SII Homologación'
              : ' · Solo emisión simulada (sin envío real al SII)'}
          </>
        )}
        {isDemo && (
          <>
            Cuenta <strong>Demo</strong>
            {status.allow_homologation
              ? ' · Habilitado para SII Homologación'
              : ' · Solo emisión simulada (sin envío real al SII)'}
          </>
        )}
      </span>
    </div>
  )
}

// ─── Navegación ───────────────────────────────────────────────────────────────

// Hoja de navegación (enlace real) o separador de sección dentro de un grupo.
type NavLeaf = { to: string; icon: React.ElementType; label: string; moduleKey?: string }
type NavHeading = { heading: string }
type NavChild = NavLeaf | NavHeading

const isHeading = (c: NavChild): c is NavHeading => 'heading' in c

interface NavItem {
  to: string
  icon: React.ElementType
  label: string
  moduleKey?: string  // clave de módulo para check disabled_modules
  children?: NavChild[]
}

// Mapeo ruta → módulo de permisos (para filtrar sidebar)
const ROUTE_MODULE_MAP: Record<string, string> = {
  '/sales/documents': 'sales.documents',
  '/sales/orders': 'sales.orders',
  '/sales/templates': 'sales.templates',
  '/sales/customers': 'sales.customers',
  '/sales/sales-reps': 'sales.customers',
  '/sales/products': 'sales.products',
  '/sales/catalog/categories': 'sales.products',
  '/sales/catalog/attributes': 'sales.products',
  '/sales/catalog/relationships': 'sales.products',
  '/sales/collections': 'sales.collections',
  '/sales/subscriptions': 'sales.subscriptions',
  '/sales/contracts': 'sales.contracts',
  '/sales/guides': 'sales.guides',
  '/sales/commissions': 'sales.commissions',
  '/sales/forecast': 'sales.forecast',
  '/purchases/suppliers': 'purchases.suppliers',
  '/purchases/orders': 'purchases.orders',
  '/purchases/invoices': 'purchases.invoices',
  '/purchases/payments': 'purchases.payments',
  '/purchases/received-dtes': 'dte_inbound',
  '/purchases/reports': 'purchases',
  '/accounting/accounts': 'accounting.accounts',
  '/accounting/cost-centers': 'accounting.cost_centers',
  '/accounting/vouchers': 'accounting.vouchers',
  '/accounting/reconciliation': 'accounting.reconciliation',
  '/accounting/settings': 'accounting.defaults',
  '/accounting/audit': 'accounting.vouchers',
  '/accounting/expenses': 'accounting.expenses',
  '/accounting/budgets': 'accounting.budgets',
  '/accounting/fixed-assets': 'accounting.fixed_assets',
  '/accounting/period-locks': 'accounting.period_lock',
  '/accounting/fiscal-years': 'accounting.fiscal_year',
  '/accounting/reports': 'accounting.vouchers',
  '/accounting/intl-invoices': 'intl_invoices',
  '/payroll/employees': 'payroll.employees',
  '/payroll/contracts': 'payroll.contracts',
  '/payroll/documents': 'hr_documents',
  '/payroll/org-chart': 'payroll.org_chart',
  '/payroll/periods': 'payroll.periods',
  '/payroll/payslips': 'payroll.payslips',
  '/payroll/vacations': 'payroll.vacations',
  '/payroll/terminations': 'payroll.terminations',
  '/payroll/bonuses': 'payroll',
  '/payroll/deductions': 'payroll.payslips',
  '/payroll/indicators': 'payroll',
  '/payroll/declarations': 'payroll.periods',
  '/payroll/honorarios': 'honorarios',
  '/payroll/assets': 'hr_assets',
  '/payroll/settings': 'payroll',
  '/receivables/collections': 'receivables',
  '/receivables/aging': 'receivables.aging',
  '/receivables/credits': 'receivables.collections',
  '/receivables/statements': 'receivables.statements',
  '/receivables/reminders': 'receivables.reminders',
  '/receivables/cash-flow': 'receivables.cash_flow',
  '/receivables/collection-config': 'receivables.collection_config',
  '/payables/payments': 'payables',
  '/payables/aging': 'payables.aging',
  '/payables/statements': 'payables.statements',
  '/payables/schedule': 'payables',
  '/payables/cash-flow': 'payables.cash_flow',
  '/inventory/stock': 'inventory.movements',
  '/inventory/warehouses': 'inventory.warehouses',
  '/inventory/movements': 'inventory.movements',
  '/inventory/counts': 'inventory.counts',
  '/inventory/transfers': 'inventory.transfers',
  '/pos/stores': 'pos.stores',
  '/pos/terminals': 'pos.terminals',
  '/pos/folios': 'pos.folios',
  '/pos/sales': 'pos.sales',
  '/pos/operators': 'pos.operators',
  '/pos/cash': 'pos.cash',
  '/pos/agent': 'pos.terminals',
  '/pos/rules': 'pos.terminals',
  '/treasury/entities': 'treasury.entities',
  '/treasury/operations': 'treasury.operations',
  '/treasury/reports': 'treasury.operations',
  '/reports': 'reports',
  '/users': 'users',
  '/config': 'config',
  '/config/profile': 'config.profile',
  '/config/localization': 'config.localization',
  '/config/currency': 'config.currency',
  '/config/folios': 'config.folios',
  '/config/fields': 'config.fields',
  '/config/templates': 'config.templates',
  '/config/certificates': 'config.certificates',
  '/config/invoicer': 'config.invoicer',
  '/config/gateways': 'config.gateways',
  '/config/sso': 'config.sso',
  '/config/api-keys': 'config.api_keys',
  '/config/dte-types': 'config.dte_types',
  '/config/hr-documents': 'config.hr_documents',
  '/config/notifications': 'config.notifications',
  '/config/audit-log': 'config.audit_log',
  '/config/historical-import': 'config.historical_import',
}

const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  {
    to: '/sales',
    icon: ShoppingCart,
    label: 'Ventas',
    // Ciclo de ingresos completo (order-to-cash): facturación → cobranza →
    // factoring. Cobranza vive físicamente en /receivables/* y Factoring en
    // /treasury/* (módulos de permiso intactos: receivables / treasury), pero
    // se presentan aquí como parte del mismo flujo de trabajo.
    children: [
      { to: '/sales/documents', icon: FileText, label: 'Documentos' },
      { to: '/sales/orders', icon: ClipboardList, label: 'Pedidos' },
      { to: '/sales/templates', icon: Copy, label: 'Plantillas' },
      { to: '/sales/subscriptions', icon: Repeat, label: 'Suscripciones', moduleKey: 'subscriptions' },
      { to: '/sales/contracts', icon: FileCheck, label: 'Contratos' },
      { to: '/sales/guides', icon: Truck, label: 'Guías de Despacho' },
      { to: '/sales/customers', icon: UserSquare2, label: 'Clientes' },
      { to: '/sales/sales-reps', icon: Briefcase, label: 'Vendedores', moduleKey: 'sales.customers' },
      { heading: 'Cobranza' },
      { to: '/receivables/collections', icon: CreditCard, label: 'Cobranza', moduleKey: 'receivables' },
      { to: '/receivables/aging', icon: BarChart3, label: 'Antigüedad', moduleKey: 'receivables' },
      { to: '/receivables/statements', icon: FileText, label: 'Estados de Cuenta', moduleKey: 'receivables' },
      { to: '/receivables/reminders', icon: Mail, label: 'Recordatorios', moduleKey: 'receivables' },
      { to: '/receivables/credits', icon: Receipt, label: 'Créditos pendientes', moduleKey: 'receivables' },
      { to: '/receivables/cash-flow', icon: DollarSign, label: 'Flujo Proyectado', moduleKey: 'receivables' },
      { to: '/receivables/collection-config', icon: Settings2, label: 'Config. Cobranza', moduleKey: 'receivables' },
      { heading: 'Factoring' },
      { to: '/treasury/operations', icon: FileText, label: 'Operaciones', moduleKey: 'treasury' },
      { to: '/treasury/entities', icon: Building2, label: 'Entidades', moduleKey: 'treasury' },
      { to: '/treasury/reports', icon: BarChart3, label: 'Reportes', moduleKey: 'treasury' },
      { heading: 'Análisis comercial' },
      { to: '/sales/commissions', icon: DollarSign, label: 'Comisiones', moduleKey: 'commissions' },
      { to: '/sales/forecast', icon: BarChart3, label: 'Forecast', moduleKey: 'forecast' },
    ],
  },
  {
    to: '/sales/products',
    icon: Boxes,
    label: 'Catálogo',
    children: [
      { to: '/sales/products', icon: Package, label: 'Productos' },
      { to: '/sales/catalog/categories', icon: FolderOpen, label: 'Categorías' },
      { to: '/sales/catalog/attributes', icon: Tags, label: 'Atributos' },
      { to: '/sales/catalog/relationships', icon: ArrowLeftRight, label: 'Compatibilidades' },
    ],
  },
  {
    to: '/purchases',
    icon: ShoppingBag,
    label: 'Compras',
    // Ciclo de egresos completo (procure-to-pay): OC → factura → pago. Los pagos
    // y CxP viven en /payables/* (módulo de permiso 'payables' intacto) y se
    // presentan aquí como parte del mismo flujo.
    children: [
      { to: '/purchases/invoices', icon: FileText, label: 'Facturas' },
      { to: '/purchases/suppliers', icon: Truck, label: 'Proveedores' },
      { to: '/purchases/orders', icon: ClipboardList, label: 'Órdenes de Compra' },
      { to: '/purchases/received-dtes', icon: FileCheck, label: 'DTEs Recibidos' },
      { heading: 'Pagos' },
      { to: '/payables/payments', icon: Banknote, label: 'Pagos', moduleKey: 'payables' },
      { to: '/payables/aging', icon: BarChart3, label: 'Antigüedad', moduleKey: 'payables' },
      { to: '/payables/statements', icon: FileText, label: 'Estados de Cuenta', moduleKey: 'payables' },
      { to: '/payables/schedule', icon: Calendar, label: 'Calendario de Pagos', moduleKey: 'payables' },
      { to: '/payables/cash-flow', icon: DollarSign, label: 'Flujo Proyectado', moduleKey: 'payables' },
      { heading: 'Análisis' },
      { to: '/purchases/reports', icon: BarChart3, label: 'Reportes' },
    ],
  },
  {
    to: '/accounting',
    icon: BookOpen,
    label: 'Contabilidad',
    children: [
      { to: '/accounting/vouchers', icon: FileText, label: 'Comprobantes' },
      { to: '/accounting/expenses', icon: FileText, label: 'Gastos' },
      { to: '/accounting/intl-invoices', icon: Globe, label: 'Invoices Internacionales', moduleKey: 'intl_invoices' },
      { to: '/accounting/audit', icon: Shield, label: 'Cruce Contable' },
      { heading: 'Plan y estructura' },
      { to: '/accounting/accounts', icon: BarChart3, label: 'Plan de Cuentas' },
      { to: '/accounting/cost-centers', icon: Building2, label: 'Centros de Costo' },
      { to: '/accounting/fixed-assets', icon: Building2, label: 'Activo Fijo' },
      { heading: 'Reportes' },
      { to: '/accounting/reports', icon: BookOpen, label: 'Reportes' },
      { to: '/accounting/budgets', icon: BarChart3, label: 'Presupuestos' },
      { heading: 'Cierre y control' },
      { to: '/accounting/reconciliation', icon: Landmark, label: 'Conciliación' },
      { to: '/accounting/period-locks', icon: Lock, label: 'Bloqueo de Períodos', moduleKey: 'accounting.period_lock' },
      { to: '/accounting/fiscal-years', icon: Calendar, label: 'Cierre de Ejercicio', moduleKey: 'accounting.fiscal_year' },
      { to: '/accounting/settings', icon: Settings2, label: 'Configuración' },
    ],
  },
  {
    to: '/inventory',
    icon: Package,
    label: 'Inventario',
    moduleKey: 'inventory',
    children: [
      { to: '/inventory/stock', icon: Package, label: 'Stock' },
      { to: '/inventory/warehouses', icon: Building2, label: 'Bodegas' },
      { to: '/inventory/movements', icon: ArrowLeftRight, label: 'Movimientos' },
      { to: '/inventory/counts', icon: ClipboardList, label: 'Conteos' },
      { to: '/inventory/transfers', icon: Truck, label: 'Transferencias' },
    ],
  },
  {
    // El POS externo (equipos con credencial propia). La venta de mostrador
    // desde la web es otra cosa: `sales.pos`, en el carrito de Ventas.
    to: '/pos',
    icon: Store,
    label: 'Punto de venta',
    moduleKey: 'pos',
    children: [
      { to: '/pos/stores', icon: Store, label: 'Locales' },
      { to: '/pos/terminals', icon: MonitorSmartphone, label: 'Terminales' },
      { to: '/pos/folios', icon: Hash, label: 'Folios' },
      { to: '/pos/sales', icon: Receipt, label: 'Ventas' },
      { to: '/pos/cash', icon: Wallet, label: 'Cajas' },
      { to: '/pos/operators', icon: UserCog, label: 'Operadores' },
      { to: '/pos/rules', icon: ScanBarcode, label: 'POS Clobi' },
      { to: '/pos/agent', icon: Printer, label: 'Clobi Conector' },
    ],
  },
  {
    to: '/payroll',
    icon: Users,
    label: 'Nómina',
    moduleKey: 'payroll',
    children: [
      { to: '/payroll/employees', icon: UserSquare2, label: 'Empleados' },
      { to: '/payroll/contracts', icon: FileText, label: 'Contratos' },
      { to: '/payroll/documents', icon: FileSignature, label: 'Documentos' },
      { to: '/payroll/org-chart', icon: Network, label: 'Organigrama' },
      { to: '/payroll/periods', icon: Calendar, label: 'Períodos' },
      { to: '/payroll/payslips', icon: CreditCard, label: 'Liquidaciones' },
      { heading: 'Haberes y descuentos' },
      { to: '/payroll/bonuses', icon: Gift, label: 'Bonos' },
      { to: '/payroll/deductions', icon: TrendingDown, label: 'Descuentos y Créditos' },
      { to: '/payroll/honorarios', icon: Receipt, label: 'Honorarios (BHE)', moduleKey: 'honorarios' },
      { heading: 'Ausencias y término' },
      { to: '/payroll/vacations', icon: Globe, label: 'Vacaciones' },
      { to: '/payroll/terminations', icon: UserSquare2, label: 'Finiquitos' },
      { to: '/payroll/assets', icon: Paperclip, label: 'Activos' },
      { heading: 'Cumplimiento y config' },
      { to: '/payroll/indicators', icon: Activity, label: 'Indicadores' },
      { to: '/payroll/declarations', icon: FileCheck, label: 'Declaraciones' },
      { to: '/payroll/settings', icon: Settings, label: 'Configuración' },
    ],
  },
  { to: '/reports', icon: BarChart3, label: 'Centro de Reportes', moduleKey: 'reports' },
]

const BOTTOM_NAV: NavItem[] = [
  { to: '/users', icon: Users, label: 'Usuarios' },
  {
    to: '/config',
    icon: Settings,
    label: 'Configuración',
    children: [
      { to: '/config/profile', icon: Building2, label: 'Perfil de Empresa' },
      { to: '/config/localization', icon: Globe, label: 'Localización' },
      { to: '/config/currency', icon: DollarSign, label: 'Tipos de Cambio' },
      { to: '/config/folios', icon: Hash, label: 'Folios' },
      { heading: 'Documentos' },
      { to: '/config/fields', icon: ListPlus, label: 'Campos' },
      { to: '/config/templates', icon: FileDown, label: 'Plantillas PDF' },
      { to: '/config/dte-types', icon: FileText, label: 'Documentos DTE' },
      { to: '/config/hr-documents', icon: FileText, label: 'Documentos de RRHH' },
      { heading: 'Integraciones' },
      { to: '/config/invoicer', icon: Zap, label: 'Facturador' },
      { to: '/config/gateways', icon: CreditCard, label: 'Pasarelas de Pago' },
      { to: '/config/sso', icon: ShieldAlert, label: 'SSO' },
      { to: '/config/api-keys', icon: KeyRound, label: 'API Keys' },
      { heading: 'Seguridad y datos' },
      { to: '/config/certificates', icon: Shield, label: 'Certificados' },
      { to: '/config/notifications', icon: Bell, label: 'Notificaciones' },
      { to: '/config/audit-log', icon: Shield, label: 'Bitácora' },
      { to: '/config/historical-import', icon: Upload, label: 'Importación Histórica' },
    ],
  },
]

// Filtra los children de un grupo del sidebar por módulo activo + permiso, y
// elimina los separadores de sección que queden huérfanos (sin ninguna hoja
// visible debajo). Los headings siempre pasan el primer filtro y se podan en el
// segundo paso para no dejar títulos sueltos cuando RBAC oculta toda su sección.
function filterNavChildren(
  children: NavChild[],
  canPerm: (mod: string, action: string) => boolean,
  isModuleEnabled: (mod: string) => boolean,
): NavChild[] {
  const kept = children.filter(c => {
    if (isHeading(c)) return true
    if (c.moduleKey && !isModuleEnabled(c.moduleKey)) return false
    const mod = ROUTE_MODULE_MAP[c.to]
    return !mod || canPerm(mod, 'view')
  })
  return kept.filter((c, i) => {
    if (!isHeading(c)) return true
    // Conservar el heading solo si lo sigue al menos una hoja antes del próximo.
    const next = kept[i + 1]
    return next !== undefined && !isHeading(next)
  })
}

// Agrupa los children (ya filtrados) en secciones para el mega-menú. La primera
// tanda de hojas sin heading propio toma `fallbackHeading` (el nombre del grupo).
type NavSection = { heading: string; items: NavLeaf[] }
function groupNavSections(children: NavChild[], fallbackHeading: string): NavSection[] {
  const sections: NavSection[] = []
  let current: NavSection | null = null
  for (const c of children) {
    if (isHeading(c)) {
      current = { heading: c.heading, items: [] }
      sections.push(current)
    } else {
      if (!current) {
        current = { heading: fallbackHeading, items: [] }
        sections.push(current)
      }
      current.items.push(c)
    }
  }
  return sections.filter(s => s.items.length > 0)
}

// ─── NavItem con flyout submenu ───────────────────────────────────────────────

// Tooltip ligero (NO menú): pastilla oscura con flecha, no interactivo
// (pointer-events-none). Se usa para las hojas del sidebar colapsado, donde
// solo hay que rotular el ícono, no ofrecer un submenú.
function SidebarTooltip({ label, anchorRect }: { label: string; anchorRect: DOMRect }) {
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ top: anchorRect.top, left: anchorRect.right + 10 })

  useEffect(() => {
    if (!ref.current) return
    const h = ref.current.offsetHeight
    const viewH = window.innerHeight
    let top = anchorRect.top + anchorRect.height / 2 - h / 2
    top = Math.max(8, Math.min(top, viewH - h - 8))
    setPos({ top, left: anchorRect.right + 10 })
  }, [anchorRect])

  return createPortal(
    <div
      ref={ref}
      style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
      className="pointer-events-none"
      role="tooltip"
    >
      <div className="relative bg-slate-900 text-white/90 text-xs font-medium px-2.5 py-1.5 rounded-md shadow-lg whitespace-nowrap">
        <span className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 rotate-45 bg-slate-900" />
        {label}
      </div>
    </div>,
    document.body,
  )
}

// Cuando el sidebar está colapsado se muestra el isotipo de Clobi; al hacer hover
// se reemplaza por el ícono de panel + tooltip "Abrir barra lateral" (indica la acción).
function CollapsedSidebarToggle({ onOpen }: { onOpen: () => void }) {
  const btnRef = useRef<HTMLButtonElement>(null)
  const [hovered, setHovered] = useState(false)
  const rect = btnRef.current?.getBoundingClientRect()
  return (
    <div
      className="hidden md:block"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        ref={btnRef}
        onClick={onOpen}
        aria-label="Abrir barra lateral"
        className="flex items-center justify-center h-9 w-9 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
      >
        {hovered ? (
          <PanelLeftOpen size={22} />
        ) : (
          <img src="/clobi-isotipo.svg" alt="Clobi" className="h-9 w-9 object-contain" />
        )}
      </button>
      {hovered && rect && <SidebarTooltip label="Abrir barra lateral" anchorRect={rect} />}
    </div>
  )
}

function FlyoutMenu({ children, anchorRect }: { children: React.ReactNode; anchorRect: DOMRect }) {
  const menuRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ top: anchorRect.top, left: anchorRect.right + 6 })

  useEffect(() => {
    if (!menuRef.current) return
    const menuH = menuRef.current.offsetHeight
    const menuW = menuRef.current.offsetWidth
    const viewH = window.innerHeight
    const viewW = window.innerWidth
    let top = anchorRect.top
    if (top + menuH > viewH - 8) {
      top = Math.max(8, viewH - menuH - 8)
    }
    // Mega-menús anchos: si se salen por la derecha, los pegamos al borde.
    let left = anchorRect.right + 6
    if (left + menuW > viewW - 8) {
      left = Math.max(8, viewW - menuW - 8)
    }
    setPos({ top, left })
  }, [anchorRect])

  return createPortal(
    <div
      ref={menuRef}
      style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
      className="bg-slate-800 border border-white/10 rounded-xl shadow-2xl py-1.5 min-w-[200px] max-h-[calc(100vh-16px)] overflow-y-auto"
    >
      {children}
    </div>,
    document.body,
  )
}

// Shared context: only one flyout open at a time
const FlyoutContext = {
  current: null as string | null,
  listeners: new Set<() => void>(),
  open(id: string) {
    this.current = id
    this.listeners.forEach(fn => fn())
  },
  close() {
    this.current = null
    this.listeners.forEach(fn => fn())
  },
}

function useFlyout(id: string) {
  const [isOpen, setIsOpen] = useState(false)
  const closeTimer = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    const listener = () => setIsOpen(FlyoutContext.current === id)
    FlyoutContext.listeners.add(listener)
    return () => { FlyoutContext.listeners.delete(listener) }
  }, [id])

  const open = useCallback(() => {
    clearTimeout(closeTimer.current)
    FlyoutContext.open(id)
  }, [id])

  const scheduleClose = useCallback(() => {
    clearTimeout(closeTimer.current)
    closeTimer.current = setTimeout(() => {
      if (FlyoutContext.current === id) FlyoutContext.close()
    }, 100)
  }, [id])

  const cancelClose = useCallback(() => {
    clearTimeout(closeTimer.current)
  }, [])

  return { isOpen, open, scheduleClose, cancelClose }
}

// Tamaño de los íconos del sidebar. Al colapsar mostramos solo el ícono, así que
// lo agrandamos para que se lea mejor. Ajustar NAV_ICON_COLLAPSED para calibrar.
const NAV_ICON_EXPANDED = 18
const NAV_ICON_COLLAPSED = 23

// En pantallas bajas cada fila del menú pasa de 40 a 32px. Medido el 2026-10-15:
// con 1366×768 (≈633px útiles) el menú abierto ya no cabía y aparecía scroll.
// Solo con puntero fino: en una pantalla táctil la fila conserva el área de toque.
const NAV_ROW_COMPACTA = '[@media(max-height:760px)_and_(pointer:fine)]:py-1.5'

function SidebarNavItem({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const location = useLocation()
  // Un item con submenú está activo SOLO si la ruta coincide con alguno de sus
  // children (o con su ruta base exacta), no por prefijo del padre. Si usáramos
  // startsWith(item.to), "Ventas" (/sales) capturaría también "Catálogo"
  // (/sales/products, /sales/catalog/*), dejando ambos resaltados a la vez.
  const { pathname } = location
  const isActive = item.children
    ? pathname === item.to ||
      item.children.some(c => !isHeading(c) && (pathname === c.to || pathname.startsWith(c.to + '/')))
    : pathname.startsWith(item.to)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const leafRef = useRef<HTMLAnchorElement>(null)
  const Icon = item.icon
  const { isOpen, open, scheduleClose, cancelClose } = useFlyout(item.to)

  if (item.children) {
    const anchorRect = triggerRef.current?.getBoundingClientRect()

    return (
      <div onMouseEnter={open} onMouseLeave={scheduleClose}>
        <button
          ref={triggerRef}
          onClick={() => isOpen ? FlyoutContext.close() : open()}
          className={`w-full flex items-center gap-3 py-2.5 ${NAV_ROW_COMPACTA} rounded-lg text-sm font-medium transition-colors
            ${collapsed ? 'justify-center px-0' : 'px-3'}
            ${isActive ? 'bg-white/15 text-white' : 'text-white/50 hover:text-white hover:bg-white/10'}`}
        >
          <Icon size={collapsed ? NAV_ICON_COLLAPSED : NAV_ICON_EXPANDED} className="shrink-0" />
          {!collapsed && (
            <>
              <span className="flex-1 text-left">{item.label}</span>
              <ChevronRight size={14} className="opacity-40" />
            </>
          )}
        </button>

        {isOpen && anchorRect && (() => {
          const sections = groupNavSections(item.children!, item.label)
          const isMega = sections.length > 1
          const renderLeaf = (child: NavLeaf) => {
            const ChildIcon = child.icon
            return (
              <NavLink
                key={child.to}
                to={child.to}
                onClick={() => FlyoutContext.close()}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-colors
                  ${isActive
                    ? 'bg-white/15 text-white'
                    : 'text-white/60 hover:text-white hover:bg-white/10'}`
                }
              >
                <ChildIcon size={15} className="shrink-0" />
                {child.label}
              </NavLink>
            )
          }
          return (
            <FlyoutMenu anchorRect={anchorRect}>
              <div onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
                {isMega ? (
                  <div className="px-3 py-2.5">
                    <p className="px-1 pb-2 mb-1 text-[11px] font-bold uppercase tracking-wider text-white/50 border-b border-white/10">
                      {item.label}
                    </p>
                    <div className="flex items-start gap-1 pt-1">
                      {sections.map((sec, si) => (
                        <div
                          key={si}
                          className={`min-w-[180px] space-y-0.5 px-1 ${si > 0 ? 'border-l border-white/5 pl-2.5 ml-1' : ''}`}
                        >
                          <p className="px-2.5 pt-0.5 pb-1.5 text-[10px] font-bold uppercase tracking-wider text-white/35">
                            {sec.heading}
                          </p>
                          {sec.items.map(renderLeaf)}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-0.5 px-1.5">
                    <p className="px-2.5 pt-1 pb-1.5 text-[10px] font-bold uppercase tracking-wider text-white/30">
                      {item.label}
                    </p>
                    {sections[0]?.items.map(renderLeaf)}
                  </div>
                )}
              </div>
            </FlyoutMenu>
          )
        })()}
      </div>
    )
  }

  // Hoja (sin submenú): cuando el sidebar está colapsado mostramos su nombre en
  // un panel-etiqueta al hover (igual que el flyout de los grupos), porque el
  // ícono solo no indica adónde lleva.
  const leafRect = leafRef.current?.getBoundingClientRect()
  return (
    <div onMouseEnter={collapsed ? open : undefined} onMouseLeave={collapsed ? scheduleClose : undefined}>
      <NavLink to={item.to} ref={leafRef}
        className={({ isActive }) =>
          `flex items-center gap-3 py-2.5 ${NAV_ROW_COMPACTA} rounded-lg text-sm font-medium transition-colors
          ${collapsed ? 'justify-center px-0' : 'px-3'}
          ${isActive ? 'bg-white/15 text-white' : 'text-white/50 hover:text-white hover:bg-white/10'}`
        }>
        <Icon size={collapsed ? NAV_ICON_COLLAPSED : NAV_ICON_EXPANDED} className="shrink-0" />
        {!collapsed && <span>{item.label}</span>}
      </NavLink>
      {collapsed && isOpen && leafRect && (
        <SidebarTooltip label={item.label} anchorRect={leafRect} />
      )}
    </div>
  )
}

// ─── AppLayout ────────────────────────────────────────────────────────────────

// Hook para obtener color de sidebar y código de país del tenant
function useTenantMeta() {
  const { activeTenantSchema } = useAuthStore()
  const [color, setColor] = useState<string>('')
  const [countryCode, setCountryCode] = useState<string>('')
  const [logoUrl, setLogoUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!activeTenantSchema) return
    // Leer desde localStorage para evitar flash
    const storedColor = localStorage.getItem(`zen-sidebar-color-${activeTenantSchema}`)
    if (storedColor) setColor(storedColor)
    const storedCountry = localStorage.getItem(`zen-country-code-${activeTenantSchema}`)
    if (storedCountry) setCountryCode(storedCountry)
    const storedLogo = localStorage.getItem(`zen-logo-url-${activeTenantSchema}`)
    if (storedLogo) setLogoUrl(storedLogo)

    import('@/lib/apiClient').then(({ default: apiClient }) => {
      // Cargar color y logo desde perfil
      apiClient.get('/config/profile/').then(res => {
        const c = res.data?.sidebar_color || ''
        setColor(c)
        if (c) localStorage.setItem(`zen-sidebar-color-${activeTenantSchema}`, c)
        else localStorage.removeItem(`zen-sidebar-color-${activeTenantSchema}`)
        const logo = res.data?.logo_url || null
        setLogoUrl(logo)
        if (logo) localStorage.setItem(`zen-logo-url-${activeTenantSchema}`, logo)
        else localStorage.removeItem(`zen-logo-url-${activeTenantSchema}`)
        // Aplicar branding del tenant (CSS vars de primary, accent, sidebar)
        import('@/lib/applyTenantBranding').then(({ applyTenantBranding }) => {
          applyTenantBranding(res.data || null)
        })
      }).catch(() => {})
      // Cargar código de país desde localización
      apiClient.get('/config/localization/').then(res => {
        const cc = res.data?.country_code || ''
        setCountryCode(cc)
        if (cc) localStorage.setItem(`zen-country-code-${activeTenantSchema}`, cc)
        else localStorage.removeItem(`zen-country-code-${activeTenantSchema}`)
      }).catch(() => {})
    })
  }, [activeTenantSchema])

  return { sidebarColor: color, countryCode, logoUrl }
}

// Preferencia de UI: el sidebar arranca COLAPSADO por defecto (los flujos de
// trabajo viven a todo el ancho). Se persiste por navegador para no frustrar a
// quien lo deje abierto. Default colapsado cuando no hay preferencia guardada.
const SIDEBAR_PREF_KEY = 'zen-sidebar-open'

export function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const stored = localStorage.getItem(SIDEBAR_PREF_KEY)
    return stored === null ? false : stored === '1'
  })
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  // Toggle manual del sidebar: setea estado + persiste la preferencia. El efecto
  // de la Vista 360 usa setSidebarOpen "crudo" (sin persistir) para no pisar la
  // preferencia del usuario con un colapso automático y temporal.
  const persistSidebar = useCallback((open: boolean) => {
    setSidebarOpen(open)
    localStorage.setItem(SIDEBAR_PREF_KEY, open ? '1' : '0')
  }, [])
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const disparadorRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  useCierreAlClicAfuera(userMenuOpen, [disparadorRef, panelRef], () => setUserMenuOpen(false))
  // Vista 360: tiene su propio rail de documentos como 1ª columna, así que el
  // sidebar de la app se ve redundante. Lo colapsamos al entrar y restauramos
  // el estado previo al salir (sin pisar el toggle manual mientras se navega
  // dentro del 360).
  const preThree60Sidebar = useRef<boolean | null>(null)
  const { user, activeTenant, tenants, activeTenantSchema, employeeRecord, firm } = useAuthStore()
  const { mutate: logout } = useLogout()
  // Rehidrata la señal `firm` (y employee_record) desde /auth/me en cada carga del
  // shell / cambio de subdominio: la cookie zen_session NO las trae, y el login
  // las setea solo en la sesión que lo originó. Sin esto, el portal del Estudio
  // Contable no aparece tras un salto cross-subdominio (SSO) ni en una sesión
  // hidratada desde cookie.
  useMe()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { sidebarColor, countryCode, logoUrl } = useTenantMeta()
  const { can: canPerm, isModuleEnabled } = usePermission()

  // Colapsar el sidebar en la Vista 360 (rail propio); restaurar al salir.
  const isThree60 = /^\/sales\/documents\/[^/]+\/360\b/.test(location.pathname)
  useEffect(() => {
    if (isThree60) {
      if (preThree60Sidebar.current === null) preThree60Sidebar.current = sidebarOpen
      setSidebarOpen(false)
    } else if (preThree60Sidebar.current !== null) {
      setSidebarOpen(preThree60Sidebar.current)
      preThree60Sidebar.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isThree60])

  // Sincronizar disabled_modules desde la API del perfil al store.
  // La fuente de verdad es siempre el perfil de la empresa (DB), no el JWT,
  // ya que el JWT puede estar desactualizado si el refresh token expiró.
  const { data: profileData } = useCompanyProfile()
  useEffect(() => {
    if (!profileData || !activeTenantSchema) return
    const store = useAuthStore.getState()
    const freshModules = profileData.disabled_modules ?? []
    const currentModules = store.activeTenant?.disabled_modules
    // Actualizar siempre que el valor del perfil difiera del store
    const isDifferent =
      currentModules === undefined ||
      JSON.stringify([...freshModules].sort()) !== JSON.stringify([...(currentModules)].sort())
    if (isDifferent) {
      const updatedTenants = store.tenants.map(t =>
        t.schema === activeTenantSchema
          ? { ...t, disabled_modules: freshModules }
          : t
      )
      store.hydrateFromSession({ user: store.user!, tenants: updatedTenants })
      store.setActiveTenant(activeTenantSchema)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(profileData?.disabled_modules), activeTenantSchema])

  // WebSocket real-time connection
  useWebSocket()

  // Cerrar menú móvil al navegar
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  const prevTenantRef = useRef<string | null>(null)
  // Limpiar caché de queries SOLO al cambiar de tenant (no en cada mount/F5)
  useEffect(() => {
    if (prevTenantRef.current !== null && prevTenantRef.current !== activeTenantSchema) {
      queryClient.clear()
    }
    prevTenantRef.current = activeTenantSchema ?? null
  }, [activeTenantSchema, queryClient])

  const handleSwitchTenant = () => {
    if (tenants.length > 1) navigate('/auth/select-tenant')
  }

  return (
    <TourProvider>
    {/* El fondo que hace visible el vidrio: una vez por shell y detras de todo.
        Es `fixed`, asi que no participa del layout ni scrollea. Sin el, las
        superficies translucidas del sistema son indistinguibles del canvas. */}
    <OrbField />
    <div className="relative z-10 flex h-screen overflow-hidden">
      {/* ── Backdrop móvil ── */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* ── Sidebar ── */}
      <aside
        data-tour="sidebar"
        className={`
          flex flex-col transition-all duration-300 shrink-0 z-50
          fixed md:relative inset-y-0 left-0
          border-r border-white/10
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0
          ${sidebarOpen ? 'w-60' : 'md:w-16 w-60'}
        `}
        style={{ backgroundColor: sidebarColor || 'rgb(var(--color-sidebar))' }}
      >

        {/* Logo App */}
        <div data-tour="app-logo" className={`flex items-center h-14 sm:h-16 border-b border-white/10 ${sidebarOpen ? 'justify-between px-4' : 'justify-center px-2'}`}>
          {sidebarOpen ? (
            <>
              <img src="/clobi-logo-white-compacto.svg" alt="Clobi" className="h-10 object-contain" />
              <button onClick={() => { persistSidebar(false); setMobileMenuOpen(false) }}
                className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors hidden md:block">
                <X size={16} />
              </button>
              <button onClick={() => setMobileMenuOpen(false)}
                className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors md:hidden">
                <X size={18} />
              </button>
            </>
          ) : (
            <CollapsedSidebarToggle onOpen={() => persistSidebar(true)} />
          )}
        </div>

        {/* Tenant activo */}
        {sidebarOpen && activeTenant && (
          <div className="px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              {logoUrl ? (
                <img src={logoUrl} alt="" className="w-6 h-6 rounded object-contain bg-white/10 shrink-0" />
              ) : (
                <div className="w-6 h-6 rounded bg-white/10 flex items-center justify-center shrink-0">
                  <Building2 size={12} className="text-white/60" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs text-white/80 font-medium truncate">{activeTenant.name}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <p className="text-xs text-white/40 truncate">{activeTenant.schema}</p>
                  {countryCode && (
                    <span className="text-xs font-semibold text-white/50 bg-white/10 px-1.5 py-0.5 rounded shrink-0">
                      {countryCode}
                    </span>
                  )}
                </div>
              </div>
              {tenants.length > 1 && (
                <button onClick={handleSwitchTenant}
                  className="p-1 text-white/40 hover:text-white/70 transition-colors" title="Cambiar empresa" aria-label="Cambiar empresa">
                  <RefreshCw size={12} />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Nav principal (filtrado por permisos y módulos activos) */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {/* Estudio Contable: capa transversal a las empresas (solo si el usuario
              pertenece a un estudio). Cartera para todos; gestión solo firm_admin. */}
          {firm && (
            <>
              <SidebarNavItem
                collapsed={!sidebarOpen}
                item={{
                  to: '/firm',
                  icon: Briefcase,
                  label: 'Estudio Contable',
                  children: [
                    { to: '/firm/portfolio', icon: BarChart3, label: 'Cartera' },
                    ...(firm.firm_role === 'firm_admin'
                      ? [
                          { to: '/firm/clients', icon: Building2, label: 'Empresas' },
                          { to: '/firm/staff', icon: Users, label: 'Equipo' },
                          { to: '/firm/requests', icon: Inbox, label: 'Solicitudes' },
                        ]
                      : []),
                  ],
                }}
              />
              <div className="border-b border-white/10 my-1.5" />
            </>
          )}
          {NAV_ITEMS.map(item => {
            // Ocultar módulo desactivado por el tenant
            if (item.moduleKey && !isModuleEnabled(item.moduleKey)) return null
            // Filtrar item sin children si no tiene permiso
            if (!item.children) {
              const mod = ROUTE_MODULE_MAP[item.to]
              if (mod && !canPerm(mod, 'view')) return null
            }
            // Filtrar children sin permiso ni módulo activo (poda headings huérfanos)
            const filtered = item.children
              ? { ...item, children: filterNavChildren(item.children, canPerm, isModuleEnabled) }
              : item
            // Ocultar grupo si no tiene children visibles
            if (item.children && filtered.children?.length === 0) return null
            return <SidebarNavItem key={item.to} item={filtered} collapsed={!sidebarOpen} />
          })}
        </nav>

        {/* Nav inferior */}
        <div className="p-3 border-t border-white/10 space-y-1">
          {BOTTOM_NAV.map(item => {
            // Item sin children: filtrar por su propio módulo.
            if (!item.children) {
              const mod = ROUTE_MODULE_MAP[item.to]
              if (mod && !canPerm(mod, 'view')) return null
              return <SidebarNavItem key={item.to} item={item} collapsed={!sidebarOpen} />
            }
            // Item con children (Configuración): filtrar cada sub-sección por su
            // módulo granular (config.<section>). canPerm hace fallback a 'config',
            // así un admin con `config` ve todo y un rol con solo `config.folios`
            // ve solo Folios.
            const filtered = {
              ...item,
              children: filterNavChildren(item.children, canPerm, isModuleEnabled)
                // La numeración se llama distinto según el país: «Folios» donde
                // el rango lo autoriza la autoridad (CAF del SII) y «Series»
                // donde el correlativo lo lleva el emisor (Perú).
                .map(c =>
                  'to' in c && c.to === '/config/folios'
                    ? { ...c, label: etiquetasNumeracion(countryCode).nav }
                    : c
                ),
            }
            if (filtered.children.length === 0) return null
            return <SidebarNavItem key={item.to} item={filtered} collapsed={!sidebarOpen} />
          })}
          {/* El portal del colaborador (Caso B) ya NO se muestra en el sidebar del
              ERP: se abre en una pestaña aparte desde el menú de usuario (dropdown),
              con su propio layout. Ver ErpPortalLayout. */}
          {/* «Plataforma» y «Certificación SII» (solo administradores de plataforma)
              viven en el menú de usuario: son herramientas internas de Zen y acá
              le quitaban ~97px al área desplazable del menú (2026-10-15). */}
        </div>
      </aside>

      {/* ── Main ── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <EnvBanner />
        {/* Aviso de ventana de mantenimiento — va SOBRE el header para que se vea
            en toda la app, sin importar en qué pantalla esté el usuario. */}
        <MaintenanceBanner />
        {/* Demora del SII: solo a quien tiene envíos esperando (lo decide el backend). */}
        <SiiStatusBanner />
        {/* TopBar */}
        <header className="cl-topbar flex items-center justify-between h-14 sm:h-16 px-3 sm:px-6 border-b border-divider shrink-0">
          {/* Hamburger mobile */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 rounded-lg text-content-muted hover:bg-soft md:hidden"
          >
            <Menu size={20} />
          </button>
          {/* Switcher de empresa (solo si el usuario tiene 2+ empresas) */}
          <div className="hidden md:flex items-center min-w-0">
            <WorkspaceSwitcher />
          </div>
          <div className="flex items-center gap-3">
            <span data-tour="theme-toggle"><QuickThemeToggle /></span>
            <span data-tour="updates-trigger"><UpdatesTrigger /></span>
            {canPerm('sales.orders', 'create') && (
              <span data-tour="cart-bell"><CartBell /></span>
            )}
            <span data-tour="notification-bell"><NotificationBell /></span>
            <div className="relative" data-tour="user-menu">
            <button ref={disparadorRef} onClick={() => setUserMenuOpen(p => !p)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-soft transition-colors">
              <div className="w-7 h-7 rounded-full bg-accent-dark flex items-center justify-center">
                <span className="text-white text-xs font-semibold">
                  {user?.full_name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              {user && (
                <div className="text-left hidden sm:block">
                  <p className="text-sm font-medium text-content leading-none">{user.full_name || user.email}</p>
                  <p className="text-xs text-content-subtle mt-0.5">
                    {user.roles?.map((r) => ROLE_NAMES[r as RoleType] || r).join(', ')}
                  </p>
                </div>
              )}
              <ChevronDown size={14} className="text-content-subtle" />
            </button>

            {userMenuOpen && (
              <>
                <div ref={panelRef}
                  className="absolute right-0 top-12 z-50 bg-popover border border-divider rounded-xl shadow-lg py-1 min-w-[200px] whitespace-nowrap"
                  style={{ backgroundColor: 'rgb(var(--color-popover))' }}
                >
                  <button onClick={() => { navigate('/profile'); setUserMenuOpen(false) }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-content hover:bg-soft transition-colors">
                    <User size={15} />Mi perfil
                  </button>
                  <button onClick={() => { navigate('/change-password'); setUserMenuOpen(false) }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-content hover:bg-soft transition-colors">
                    <KeyRound size={15} />Cambiar contraseña
                  </button>
                  {employeeRecord && (
                    <>
                      <div className="border-t border-divider my-1" />
                      <button onClick={() => { navigate('/portal'); setUserMenuOpen(false) }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-content hover:bg-soft transition-colors">
                        <UserSquare2 size={15} />Portal del colaborador
                      </button>
                    </>
                  )}
                  {user?.is_platform_admin && (
                    <>
                      <div className="border-t border-divider my-1" />
                      <p className="px-4 pt-1.5 pb-1 text-[10px] font-bold uppercase tracking-wider text-content-subtle">
                        Administración de Clobi
                      </p>
                      <button onClick={() => { navigate('/platform'); setUserMenuOpen(false) }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-content hover:bg-soft transition-colors">
                        <Shield size={15} />Plataforma
                      </button>
                      <button onClick={() => { navigate('/certification'); setUserMenuOpen(false) }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-content hover:bg-soft transition-colors">
                        <BadgeCheck size={15} />Certificación SII
                      </button>
                    </>
                  )}
                  <div className="border-t border-divider my-1" />
                  <button onClick={() => logout()}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-danger-content-soft hover:bg-danger-surface transition-colors">
                    <LogOut size={15} />Cerrar sesión
                  </button>
                </div>
              </>
            )}
            </div>
          </div>
        </header>

        {/* Banner cuenta Trial/Demo */}
        <AccountBanner />

        {/* Banner estacional (Fiestas Patrias, Navidad, Año Nuevo) */}
        <SeasonalBanner />

        {/* Contenido */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
    </TourProvider>
  )
}
